export default function PalmBeachLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Palm Beach &amp; South Florida
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Palm Beach, West Palm Beach, Wellington, or anywhere in South Florida, Fast Passport Center makes expedited passport processing simple — no drive to Miami, no agency lines, no guessing.
          </p>
        </div>

        {/* Editorial intro — full width, matches Naples structure */}
        <div className="bg-white border border-gray-200 rounded-lg p-7 mb-12">
          <p className="text-gray-600 text-sm leading-[1.85] mb-4">
            Palm Beach occupies a unique position among Florida communities. The island itself — bordered by the Atlantic on one side and the Intracoastal Waterway on the other — has functioned as a seasonal retreat for Northeastern families since the Gilded Age. Worth Avenue remains one of the most exclusive retail corridors in the country, and the social calendar revolves around institutions like The Breakers, the Palm Beach Yacht Club, and the Royal Poinciana Plaza.
          </p>
          <p className="text-gray-600 text-sm leading-[1.85] mb-4">
            This is not a typical snowbird destination. Palm Beach seasonal residents often maintain dual households — primary residences in Manhattan, Greenwich, or Boston, with winter estates on the island or in neighboring Wellington. Their travel patterns reflect this bifurcated lifestyle: European summers in the Cotswolds or Cap Ferrat, charity galas in London or Paris during Palm Beach's off-season, and family reunions in Switzerland or Italy that coincide with school breaks.
          </p>
          <p className="text-gray-600 text-sm leading-[1.85] mb-4">
            Wellington, just west of Palm Beach proper, adds another dimension entirely. The Winter Equestrian Festival runs from January through March and draws international competitors, trainers, and spectators from across the globe. Many Wellington residents — both the equestrian professionals and the families who follow the circuit — travel internationally between competition seasons, often discovering passport issues when they are already committed to shows in Spruce Meadows, Aachen, or Hickstead.
          </p>
          <p className="text-gray-600 text-sm leading-[1.85] mb-4">
            The maritime culture here is equally distinctive. Palm Beach Yacht Club members and Royal Poinciana Yacht Club families frequently depart on extended Caribbean itineraries or trans-Atlantic passages. Unlike commercial cruise travelers, yacht departures are not always predictable months in advance — weather windows, crew availability, and marina reservations shift. A passport discovered to be expiring mid-season can derail an entire itinerary that has been months in planning.
          </p>
          <p className="text-gray-600 text-sm leading-[1.85]">
            Fast Passport Center serves this community with the discretion and efficiency it demands. Our courier-based expedited processing means no trips to Miami, no waiting in agency lines, and no uncertainty about whether an appointment will open. Documents ship overnight from Palm Beach, West Palm Beach, or Jupiter, and our team handles submission through reserved agency access. Whether you are leaving from PBI for Paris, preparing for Wellington's show season, or departing from a marina for the Caribbean, we make sure your travel documents are ready when your lifestyle requires them.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Info + How It Works */}
          <div>
            {/* FPC Info Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Delray Beach (Nearest Office)</p>
                  <p className="text-white/60 text-xs mt-0.5">401 W Atlantic Ave #351, Delray Beach, FL 33444</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Nearest office to Palm Beach — approximately 15 miles south
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout Palm Beach County</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or office in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location, or <strong className="text-white">schedule a drop-off</strong> at our Delray Beach office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                {
                  icon: "ri-computer-line",
                  title: "Start 100% Online",
                  desc: "No office visit required to begin. Complete your order from your estate, yacht club, or office in minutes.",
                },
                {
                  icon: "ri-truck-line",
                  title: "Ship Your Documents",
                  desc: "Use overnight shipping from Palm Beach, West Palm Beach, or Jupiter — we handle the rest.",
                },
                {
                  icon: "ri-shield-check-line",
                  title: "Expert Document Review",
                  desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
                },
                {
                  icon: "ri-calendar-check-line",
                  title: "Reserved Agency Access",
                  desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
                },
              ].map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities &amp; Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {[
                  { name: "Palm Beach", zips: "33480" },
                  { name: "West Palm Beach", zips: "33401" },
                  { name: "Palm Beach Gardens", zips: "33410" },
                  { name: "Wellington", zips: "33414" },
                  { name: "Jupiter", zips: "33458" },
                ].map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in South Florida — start online, ship overnight, and we handle the rest. No need to drive to Miami.
              </p>

              {/* Internal links */}
              <div className="mt-5 pt-4 border-t border-gray-200">
                <p className="text-[#1A365D] text-xs font-bold uppercase tracking-wider mb-3">Nearby Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "Boca Raton", href: "/cities/boca-raton" },
                    { name: "Delray Beach", href: "/cities/delray-beach" },
                    { name: "Palm Beach Gardens", href: "/cities/palm-beach-gardens" },
                    { name: "Jupiter", href: "/cities/jupiter" },
                    { name: "Wellington", href: "/cities/wellington" },
                    { name: "West Palm Beach", href: "/cities/west-palm-beach" },
                  ].map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                    >
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}